import React, { useEffect, Suspense, lazy } from 'react';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom';
import { DragDropContext } from '@hello-pangea/dnd';
import { DndProvider as ReactDnd } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import store from './../store';
import { loadUser } from './../actions/auth';

import Header from './general/header';
import Load from './general/load';

/* =========================
   LAZY LOADED PAGES
========================= */

const Home = lazy(() => import('./general/home'));
const Login = lazy(() => import('./user/login'));
const Register = lazy(() => import('./user/register'));
const User = lazy(() => import('./user/user'));
const Creativo = lazy(() => import('./user/creativo'));

const Explorar = lazy(() => import('./paginas/explorar/explorar'));
const Observacion = lazy(() => import('./paginas/explorar/ver/observacion'));

const Especies = lazy(() => import('./paginas/especies/especies'));
const EspecieIndividual = lazy(() => import('./paginas/especies/especieindividual'));

const Proyectos = lazy(() => import('./paginas/proyectos/proyectos'));
const ProyectoDetalle = lazy(() => import('./paginas/proyectos/proyectodetalle'));

const Paquete = lazy(() => import('./paginas/paquete/ver/listapaquete'));
const PaqueteIndividual = lazy(() => import('./paginas/paquete/ver/paqueteindividual'));
const PaqueteFichaIndividual = lazy(() => import('./paginas/paquete/ver/ficha'));

const Panel = lazy(() => import('./dashboard/panel'));
const BuilderLayout = lazy(() => import('./dashboard/PDF/builder/builder'));

const Dex = lazy(() => import('./paginas/dex/dex'));
const DexIndividual = lazy(() => import('./paginas/dex/itemdex'));

const Terminos = lazy(() => import('./general/terminos'));
const Politicas = lazy(() => import('./general/politicas'));
const Nosotros = lazy(() => import('./general/nosotros'));
const Creditos = lazy(() => import('./general/creditos'));
const GuiaUsuario = lazy(() => import('./general/guiausuario'));
const Ecosistemas = lazy(() => import('./general/ecosistemas'));

const Verificacion = lazy(() => import('./general/verificacion'));
const Recuperarpass = lazy(() => import('./general/recuperarpass'));
const Cambiarpass = lazy(() => import('./general/cambiarpass'));

const Usuarios = lazy(() => import('./general/utils/usuarios'));
const UserProfileCard = lazy(() => import('./general/utils/userprofile'));


/* =========================
   APP CORE
========================= */

const AppContent = () => {
    const dispatch = useDispatch();
    const { user } = useSelector(state => state.user);

    useEffect(() => {
        if (localStorage.getItem('token') && !user) {
            dispatch(loadUser());
        }
    }, [dispatch, user]);

    if (process.env.NODE_ENV === 'development') {
        console.warn = () => {};
    }

    return (
        <BrowserRouter>
            <Header />

            {/* Suspense GLOBAL */}
            <Suspense fallback={<Load />}>
                <Routes>

                    <Route path='/' element={<Home />} />
                    <Route path='/login' element={<Login />} />
                    <Route path='/register' element={<Register />} />

                    <Route path='/ecosistemas' element={<Ecosistemas />} />
                    <Route path='/terminos-condiciones' element={<Terminos />} />
                    <Route path='/guia-usuario' element={<GuiaUsuario />} />
                    <Route path='/politicas-privacidad' element={<Politicas />} />
                    <Route path='/nosotros' element={<Nosotros />} />
                    <Route path='/creditos' element={<Creditos />} />

                    <Route path='/login/recuperar' element={<Recuperarpass />} />
                    <Route path='/cambiar' element={<Cambiarpass />} />

                    <Route path='/creativo/*' element={<Creativo />} />
                    <Route path='/usuario/*' element={<User />} />

                    {/* DEX */}
                    <Route path="/dex/" element={<Dex />} />
                    <Route path="/dex/:id" element={<DexIndividual />} />

                    {/* PROYECTOS */}
                    <Route path='/proyectos/' element={<Proyectos />} />
                    <Route path="/proyectos/:id" element={<ProyectoDetalle />} />

                    {/* EXPLORAR */}
                    <Route path='/explorar/*' element={<Explorar />} />
                    <Route path="/explorar/:id" element={<Observacion />} />

                    {/* ESPECIES */}
                    <Route path="/especies/" element={<Especies />} />
                    <Route path="/especies/:id" element={<EspecieIndividual />} />

                    {/* PAQUETES */}
                    <Route path='/paquete/' element={<Paquete />} />
                    <Route path='/paquete/:paqueteId' element={<PaqueteIndividual />} />
                    <Route path='/paquete/:paqueteId/ficha/:fichaId' element={<PaqueteFichaIndividual />} />

                    {/* PDF BUILDER */}
                    <Route 
                        path="/pdf-builder" 
                        element={
                            <PdfBuilderWrapper>
                                <BuilderLayout />
                            </PdfBuilderWrapper>
                        } 
                    />

                    {/* PANEL DASHBOARD */}
                    <Route 
                        path="/panel/*" 
                        element={
                            <ReactDnd backend={HTML5Backend}>
                                <Panel />
                            </ReactDnd>
                        }
                    />

                    {/* VALIDACION */}
                    <Route path='/validacion/*' element={<Verificacion />} />

                    {/* USUARIOS */}
                    <Route path='/usuarios/perfil/:username' element={<UserProfileCard />} />
                    <Route path='/usuarios/perfil' element={<Navigate to="/usuarios/naturalista" replace />} />
                    <Route path='/usuarios/:tipo' element={<Usuarios />} />
                    <Route path='/usuarios' element={<Navigate to="/usuarios/naturalista" replace />} />

                </Routes>
            </Suspense>

        </BrowserRouter>
    );
};


/* =========================
   WRAPPER PDF
========================= */

const PdfBuilderWrapper = ({ children }) => (
    <DragDropContext onDragEnd={() => {}}>
        {children}
    </DragDropContext>
);


/* =========================
   ROOT EXPORT
========================= */

const App = () => (
    <Provider store={store}>
        <AppContent />
    </Provider>
);

export default App;
